/**
 * @file 快速开始组件的 store
 * @author FengGuang(fengguang01@baidu.com)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from 'react-use';

import { IQuickStartSelectorGroup, IQuickStartItem, IQuickStartListCache } from './type';
import React from 'react';
import { log } from 'async';

// 更新选项的禁用状态
// 阻止用户做出某种选择使得结果为空。
// 本方法会遍历 quickInstallList ，根据已选项将部分未选项禁用，
// 这个过程可以看做是一种搜索过滤的过程，根据用户的选择过滤出 quickInstallList 中的一些数据
const updateDisabledState = (
    checked: string[],
    quickInstallSelector: IQuickStartSelectorGroup[],
    quickInstallList: IQuickStartItem[],
    checkedQuickInstallListCache: { [key: string]: IQuickStartItem[] }
): { disabled: string[]; checkedItem: IQuickStartItem[] } => {
    const newDisabledSet: Set<string> = new Set();
    // 遍历 quickInstallSelector 获取所有出现过的 optionKey
    quickInstallSelector.forEach(group => {
        group.list.forEach(item => {
            newDisabledSet.add(item.optionKey);
        });
    });
    // 当前选中状态对应的搜索结果
    const checkedQuickInstallList = (() => {
        const checkedIndex = checked.sort().join(',');
        if (checkedQuickInstallListCache[checkedIndex] && checkedQuickInstallListCache[checkedIndex].length > 0) {
            return checkedQuickInstallListCache[checkedIndex];
        }
        const list = quickInstallList.filter(item => {
            return checked.every(key => item.indexSet.has(key));
        });
        checkedQuickInstallListCache[checkedIndex] = list;
        return list;
    })();
    // 决定某一个group里哪些选项被禁用，是根据其他gorup选项选中状态决定
    // 遍历group，获取每一个group中选项的禁用状态
    quickInstallSelector.forEach(group => {
        const thisGroupOptionKeys = group.list.map(item => item.optionKey);
        const thisGroupOptionKeysSet = new Set(thisGroupOptionKeys);
        // 除了自己group以外的选中状态
        const checkedExpectThisGroup = (() => {
            const checkedExpectThisGroupSet = new Set(checked);
            thisGroupOptionKeys.forEach(optionKey => {
                checkedExpectThisGroupSet.delete(optionKey);
            });
            return Array.from(checkedExpectThisGroupSet);
        })();
        // 对应的搜索结果，这里对搜索结果进行缓存以便提升运行速度
        const availableQuickInstallList = (() => {
            const checkedExpectThisGroupIndex = checkedExpectThisGroup.sort().join(',');
            if (checkedQuickInstallListCache[checkedExpectThisGroupIndex]) {
                return checkedQuickInstallListCache[checkedExpectThisGroupIndex];
            }
            const list = quickInstallList.filter(item => {
                return checkedExpectThisGroup.every(key => item.indexSet.has(key));
            });
            checkedQuickInstallListCache[checkedExpectThisGroupIndex] = list;
            return list;
        })();
        const thisGroupEnableOptionKeysSet = new Set<string>();
        availableQuickInstallList.forEach(item => {
            item.indexSet.forEach(key => {
                if (thisGroupOptionKeysSet.has(key)) {
                    thisGroupEnableOptionKeysSet.add(key);
                }
            });
        });
        thisGroupEnableOptionKeysSet.forEach(key => {
            newDisabledSet.delete(key);
        });
    });
    // 判断当前选中状态，如果符合条件，则将cpu选项禁用
    const arraysAreEqual = (arr1: string[]) => {
        const selectArray = ['macos', 'nvidia', '30version', 'pip'];
        const targetArray = ['macos', 'linux', 'windows', 'otheros', 'nvidia', 'pip', '30version', 'develop'];
        const cudaArray = ['cuda117', 'cuda118', 'cuda120', 'cuda112', 'cuda116'];
        if (arr1.length !== selectArray.length + 1) {
            return false;
        }

        for (let i = 0; i < arr1.length; i++) {
            if (!targetArray.includes(arr1[i]) && !cudaArray.includes(arr1[i])) {
                return false;
            }
        }

        return true;
    }
    if (arraysAreEqual(checked)) {
        newDisabledSet.delete('cpu');
    }

    // 芯片厂商保证始终可点
    Array.from(newDisabledSet).forEach((key) => {
        const chip = ['nvidia', 'kunlunxin', 'haiguang', 'shengteng', 'hanwuji', 'cpu', 'suiyuan', 'taichu', 'muxi', 'tianshu'];
        if (chip.includes(key)) {
            newDisabledSet.delete(key);
        }
    });
    return {
        disabled: Array.from(newDisabledSet),
        checkedItem: checkedQuickInstallList
    };
};

const useQuickInstallHooks = (localStorageName: string | undefined) => {
    const [quickInstallList, setQuickInstallList] = useState<IQuickStartItem[]>([]);
    const [quickInstallSelector, setQuickInstallSelector] = useState<IQuickStartSelectorGroup[]>([]);
    const quickInstallSelectorRef = useLatest(quickInstallSelector);
    // 选项对应的搜索结果的缓存，用来提高运行速度
    const quickInstallCacheRef = useRef<IQuickStartListCache>({});
    // 当结果列表变化的时候就清空缓存
    useMemo(() => {
        quickInstallCacheRef.current = {};
        return quickInstallList;
    }, [quickInstallList]);

    const [checked, setChecked] = useState<string[]>(() => {
        const resJson = JSON.parse(localStorage.getItem(localStorageName || 'quickInstallHooksChecked') as string);
        return Array.isArray(resJson) ? resJson : [];
    });

    // useEffect(() => {
    //     let timer: any;
    //     const getButtonDom = () => {
    //         const cuda116Dom = document.querySelector(
    //             '.paddle-quick-start-item:nth-child(4) .ant-btn:nth-child(2)'
    //         ) as HTMLButtonElement;
    //         const cuda117Dom = document.querySelector(
    //             '.paddle-quick-start-item:nth-child(4) span:nth-child(1)'
    //         ) as HTMLButtonElement;
    //         if (!cuda116Dom || !cuda117Dom) {
    //             timer = setTimeout(() => {
    //                 getButtonDom();
    //             }, 100);
    //         } else {
    //             clearTimeout(timer);
    //             if (checked.includes('paddleversion') && checked.includes('linux') && checked.includes('docker')) {
    //                 cuda116Dom.style.display = 'none';
    //                 cuda117Dom.style.display = 'block';
    //             } else {
    //                 cuda116Dom.style.display = 'block';
    //                 cuda117Dom.style.display = 'none';
    //             }
    //         }
    //     };
    //     getButtonDom();
    // }, [checked]);

    const { disabled, checkedItem } = useMemo(() => {
        return updateDisabledState(checked, quickInstallSelector, quickInstallList, quickInstallCacheRef.current);
    }, [checked, quickInstallSelector, quickInstallList]);

    const showItem = useMemo(() => {
        if (checkedItem && checkedItem.length > 0 &&
            checked.length === 1 &&
            ['kunlunxin', 'haiguang', 'shengteng', 'hanwuji', 'cpu', 'nvidia', 'suiyuan', 'taichu', 'muxi', 'tianshu'].includes(checked[0])
        ) {
            return checkedItem[0];
        }
        if (checked.length === 0) {
            return undefined;
        }
        if (checked.length >= quickInstallSelector.length) {
            return checkedItem[0];
        }
        if (checkedItem.length === 1) {
            return checkedItem[0];
        }
        return undefined;
    }, [checked, checkedItem, quickInstallSelector]);

    // 切换选项，更新展示内容
    const handleOnItemChange = useCallback(
        (event: React.MouseEvent<HTMLElement, MouseEvent>, groupKey: string, optionKey: string): void => {
            setChecked(checked => {
                const quickInstallSelector = quickInstallSelectorRef.current || [];
                let newChecked: string[] = checked;
                // 如果点击已经选中的选项，会取消选中状态
                if (checked.indexOf(optionKey) > -1) {
                    newChecked = newChecked.filter(key => key !== optionKey);
                } else {
                    // 如果是新选中，则会将同一组的其他key取消
                    const groupList = quickInstallSelector.find(group => group.groupKey === groupKey)?.list ?? [];
                    // 同一组的其他key
                    const sameGroupOptionKey = groupList.map(option => option.optionKey);
                    const sameGroupOptionKeySet = new Set(sameGroupOptionKey);
                    newChecked = [...checked.filter(key => !sameGroupOptionKeySet.has(key)), optionKey];
                }
                // 切换飞桨版本也设置固定的默认选项
                if (groupKey === '飞桨版本' &&
                    newChecked.length > 0
                ) {
                    // const platform = quickInstallSelector.find(group => group.groupKey === '计算平台');
                    // const platOptions = platform?.list;
                    // if (platOptions && platOptions.length > 0) {
                    //     const platformKey = platOptions.map(item => item.optionKey);

                    //     platformKey.forEach(key => {
                    //         if (newChecked.indexOf(key) !== -1) {
                    //             newChecked.splice(newChecked.indexOf(key), 1);
                    //         }
                    //     });
                    // }
                    // newChecked = [...newChecked, optionKey];
                    newChecked = [optionKey];
                }
                if (groupKey === 'FastDeploy 版本' &&
                    newChecked.length > 0
                ) {
                    newChecked = [optionKey];
                }
                // 判断选择的是不是国产的芯片厂商
                // const isLocalInclude = ['kunlunxin', 'haiguang', 'shengteng', 'hanwuji'].includes(optionKey);
                // if (groupKey === '芯片厂商' &&
                //     isLocalInclude &&
                //     newChecked.length > 0
                // ) {
                //     newChecked = [optionKey];
                // }
                if (groupKey === '芯片厂商' &&
                    optionKey === 'cpu' &&
                    newChecked.length > 0
                ) {
                    // const platform = quickInstallSelector.find(group => group.groupKey === '计算平台');
                    // const platOptions = platform?.list;
                    // if (platOptions && platOptions.length > 0) {
                    //     const platformKey = platOptions.map(item => item.optionKey);

                    //     platformKey.forEach(key => {
                    //         if (newChecked.indexOf(key) !== -1) {
                    //             newChecked.splice(newChecked.indexOf(key), 1);
                    //         }
                    //     });
                    // }
                    // newChecked = [...newChecked, optionKey];
                    const cudaIndex = newChecked.findIndex(v => ['cuda117', 'cuda118', 'cuda120', 'cuda112', 'cuda116'].includes(v));
                    if (cudaIndex > -1) {
                        newChecked.splice(cudaIndex, 1);
                    }
                }

                if (groupKey === '芯片厂商' &&
                    newChecked.length > 0
                ) {
                    const platform = quickInstallSelector.find(group => group.groupKey === '计算平台');
                    const platOptions = platform?.list;
                    if (platOptions && platOptions.length > 0) {
                        const platformKey = platOptions.map(item => item.optionKey);

                        platformKey.forEach(key => {
                            if (newChecked.indexOf(key) !== -1) {
                                newChecked.splice(newChecked.indexOf(key), 1);
                            }
                        });
                    }
                    // newChecked = [...newChecked, optionKey];
                }
                return newChecked;
            });
        },
        [setChecked, quickInstallSelectorRef]
    );

    useEffect(() => {
        if (typeof localStorage === 'object') {
            localStorage.setItem(localStorageName || 'quickInstallHooksChecked', JSON.stringify(checked));
        }
    }, [checked, localStorageName]);

    useEffect(() => {
        // @ts-ignore
        setChecked(checked => {
            if (checkedItem && checkedItem.length > 0 && checked.length === 1) {
                let checkedItem = [...checked];
                if (localStorageName === 'quickInstallFastDeployChecked' && ['20version', 'develop'].includes(checked[0])) {
                    checkedItem = [...checked, 'pip',];
                } else if (localStorageName === 'quickInstallHooksChecked' && ['30version', 'develop'].includes(checked[0])) {
                    checkedItem = [...checked, 'linux',];
                }

                return checkedItem;
            }

            if (checkedItem && checkedItem.length > 0 && checked.length === 1 && ['kunlunxin', 'haiguang', 'shengteng', 'hanwuji', 'suiyuan', 'taichu', 'muxi', 'tianshu'].includes(checked[0])) {
                // 如果选中的是芯片厂商，则从上到下层层的过滤出，每一个集合的第一项可选内容
                const versionItems = (() => {
                    return quickInstallSelector[0].list.map(v => v.optionKey);
                })();
                const versionSets = (() => {
                    let totalSet: any[] = [];

                    versionItems.forEach((item, i) => {
                        totalSet[i] = [];
                        // @ts-ignore
                        checkedItem.forEach(v => {
                            if (v.indexSet.has(item)) {
                                totalSet[i].push(v);
                            }
                        });
                    });
                    return totalSet.find(set => set.length > 0);
                })();
                const systemItems = (() => {
                    return quickInstallSelector[1].list.map(v => v.optionKey);
                })();
                const systemSets = (() => {
                    let totalSet: any[] = [];

                    systemItems.forEach((item, i) => {
                        totalSet[i] = [];
                        // @ts-ignore
                        versionSets.forEach(v => {
                            if (v.indexSet.has(item)) {
                                totalSet[i].push(v);
                            }
                        });
                    });
                    return totalSet.find(set => set.length > 0);
                })();
                const installItems = (() => {
                    return quickInstallSelector[2].list.map(v => v.optionKey);
                })();
                const installSets = (() => {
                    let totalSet: any[] = [];

                    installItems.forEach((item, i) => {
                        totalSet[i] = [];
                        // @ts-ignore
                        systemSets.forEach(v => {
                            if (v.indexSet.has(item)) {
                                totalSet[i].push(v);
                            }
                        });
                    });
                    return totalSet.find(set => set.length > 0);
                })();
                const platformItems = (() => {
                    return quickInstallSelector[4].list.map(v => v.optionKey);
                })();
                const platformSets = (() => {
                    let totalSet: any[] = [];

                    platformItems.forEach((item, i) => {
                        totalSet[i] = [];
                        // @ts-ignore
                        installSets.forEach(v => {
                            if (v.indexSet.has(item)) {
                                totalSet[i].push(v);
                            }
                        });
                    });
                    return totalSet.find(set => set.length > 0);
                })();
                if (Array.isArray(platformSets) && platformSets.length > 0) {
                    return Array.from(platformSets[0].indexSet);
                }
                // 针对没有计算平台匹配到的情况
                if (!Array.isArray(platformSets) &&
                    Array.isArray(installSets) && installSets.length === 1
                ) {
                    return Array.from(installSets[0].indexSet);
                }
            }
            return checked;
        });
    }, [disabled, checkedItem, quickInstallList, quickInstallSelector]);

    return {
        quickInstallSelector,
        quickInstallList,
        checked,
        disabled,
        showItem,

        setQuickInstallList,
        setQuickInstallSelector,
        setChecked,
        handleOnItemChange
    };
};

export default useQuickInstallHooks;
